import {
  ApiResponse,
  User,
  Evaluation,
  EvaluationCreateInput,
  TeamEvaluation,
  TeamEvaluationCreateInput,
  AiInsight,
} from "./types";
import { AUTH_STORAGE_KEY } from "../constants/auth";

const API_BASE = "/api";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers({ "Content-Type": "application/json" });
  if (init.headers) {
    const extra = new Headers(init.headers as HeadersInit);
    extra.forEach((value, key) => headers.set(key, value));
  }

  const token = getStoredToken();
  if (token && !headers.has("Authorization")) {
    console.log("Adding token to request:", token.substring(0, 50) + "...");
    headers.set("Authorization", `Bearer ${token}`);
  } else if (!token) {
    console.log("No token found in storage");
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  // Handle empty or non-JSON responses gracefully
  const text = await response.text();
  const payload: ApiResponse<T> = text ? JSON.parse(text) : { success: response.ok };

  if (!response.ok || payload.success === false) {
    const message = payload.error || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  // Some endpoints return plain objects without wrapping under "data"
  if (payload.data !== undefined) {
    return payload.data as T;
  }

  return payload as unknown as T;
}

export const authApi = {
  login: (email: string, password: string) =>
    request<{ token: string; user: User }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  register: (input: { name: string; email: string; password: string; role: string }) =>
    request<User>("/auth/register", {
      method: "POST",
      body: JSON.stringify(input),
    }),
};

export const evaluationApi = {
  create: (input: EvaluationCreateInput) =>
    request<Evaluation>("/evaluations", { method: "POST", body: JSON.stringify(input) }),
  getById: (id: string) => request<Evaluation>(`/evaluations/${id}`),
  listByUser: (userId: string, period?: string) =>
    request<Evaluation[]>(`/evaluations?userId=${userId}${period ? `&period=${period}` : ""}`),
};

export const teamEvaluationApi = {
  create: (input: TeamEvaluationCreateInput) =>
    request<TeamEvaluation>("/team-evaluations", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getByTeam: (teamId: string) => request<TeamEvaluation>(`/team-evaluations/${teamId}`),
};

export const aiApi = {
  summarizeIndividual: (evaluationId: string) =>
    request<AiInsight>("/ai/summarize-individual", {
      method: "POST",
      body: JSON.stringify({ evaluationId }),
    }),
  summarizeTeam: (teamId: string) =>
    request<AiInsight>("/ai/summarize-team", { method: "POST", body: JSON.stringify({ teamId }) }),
};

export const healthApi = {
  ping: () => request<{ status: string }>("/health"),
};

function getStoredToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) {
    console.log("No auth data in localStorage");
    return null;
  }
  console.log("Raw auth data length:", raw.length);
  try {
    const parsed = JSON.parse(raw) as { user: unknown; token: string | null };
    console.log("Parsed auth data, has token:", !!parsed.token);

    // Validate that token looks like a JWT (3 parts separated by dots)
    if (parsed.token && typeof parsed.token === "string") {
      const parts = parsed.token.split(".");
      console.log("Token parts count:", parts.length);
      if (parts.length === 3) {
        console.log("Token is valid JWT format");
        return parsed.token;
      }
      console.warn("Token has wrong number of parts:", parts.length);
    }
    // Invalid token format, clear storage
    console.warn("Invalid token format detected, clearing auth storage");
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  } catch (err) {
    console.warn("Failed to parse auth data, clearing storage", err);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export const apiClient = {
  async get<T>(path: string): Promise<T> {
    return request<T>(path, { method: "GET" });
  },
  async post<T>(path: string, data: unknown): Promise<T> {
    return request<T>(path, { method: "POST", body: JSON.stringify(data) });
  },
  async patch<T>(path: string, data: unknown): Promise<T> {
    return request<T>(path, { method: "PATCH", body: JSON.stringify(data) });
  },
  async delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};
